/**
 * Returns the admin-area path with the right prefix for the current app.
 *
 * `apps/web` serves admin features under `/control/*` (e.g. `/control/data-ops/health`).
 * `apps/admin` serves them under clean URLs (e.g. `/data-ops/health`).
 *
 * Shared modules (`@atlas-modules/{data-ops,server-ops,admin-ai-settings}`)
 * and the shared `@ezhire/control-panel` nav-config render in both apps
 * and need to link to admin pages without knowing which app they're in.
 *
 * Detection is build-time via `import.meta.env.VITE_ADMIN_APP`. apps/
 * admin's vite.config.ts injects `VITE_ADMIN_APP=true`; apps/web doesn't.
 * This avoids the host-detection footguns of earlier iterations (prod
 * regex was too narrow, then localhost:5273 broke on `vite preview`'s
 * 5274 port, etc.). Build-time signal is unambiguous.
 *
 * In SSR / non-browser contexts: returns the clean path. Both apps are
 * Vite SPAs (no SSR), so this branch is dead code in practice.
 *
 * NOTE: the result is still a string. `<Link to>` is typed strictly
 * against the consuming app's `routeTree.gen.ts`; callers cast with
 * `as never` to satisfy TanStack's typed-route system.
 */
export function adminAreaPath(cleanPath: string): string {
  // Vite replaces `import.meta.env.VITE_ADMIN_APP` at build time. apps/
  // admin's vite.config.ts sets it; apps/web doesn't. The string compare
  // makes the build-time replacement transparent.
  const isAdminApp =
    typeof import.meta !== 'undefined' &&
    (import.meta as { env?: { VITE_ADMIN_APP?: string } }).env?.VITE_ADMIN_APP === 'true';
  return isAdminApp ? cleanPath : `/control${cleanPath}`;
}
